import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import content from "@/lib/siteContent";
import seoRoutes from "@/lib/seoRoutes.json";

const LanguageContext = createContext({
  lang: "sk",
  setLang: () => {},
  toggle: () => {},
  t: content.sk,
  homePath: "/",
  isHome: true,
  anchor: (hash) => hash,
});

const SUPPORTED = ["sk", "en"];

const normalize = (p) => (p || "/").replace(/\/+$/, "") || "/";

/** The same page in the other language, from the route registry. */
const counterpart = (pathname, from, to) => {
  const page = seoRoutes.find((r) => normalize(r.paths[from]) === normalize(pathname));
  return page ? page.paths[to] : (to === "en" ? "/en" : "/");
};

/**
 * Route-driven i18n:
 *  - `/`     → SK          `/chata-pri-liptovskej-mare`   → SK landing
 *  - `/en`   → EN          `/en/liptovska-mara-cottage`   → EN landing
 *
 * Toggling navigates to the sibling page in the other language
 * (falls back to that language's homepage for unregistered paths).
 * `anchor(hash)` makes shared chrome (Navbar/Footer) link section anchors
 * correctly from any page: in-page on home, back-to-home elsewhere.
 */
export const LanguageProvider = ({ lang, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const safeLang = SUPPORTED.includes(lang) ? lang : "sk";
  const t = content[safeLang] || content.sk;

  const homePath = safeLang === "en" ? "/en" : "/";
  const isHome =
    normalize(location.pathname) === normalize(homePath) ||
    // Unregistered /en/* paths render the EN homepage (SPA catch-all parity).
    (safeLang === "en" && !seoRoutes.some((r) => normalize(r.paths.en) === normalize(location.pathname)));

  const anchor = (hash) => (isHome ? hash : `${homePath}${hash}`);

  const setLang = (next) => {
    if (!SUPPORTED.includes(next) || next === safeLang) return;
    navigate(`${counterpart(location.pathname, safeLang, next)}${location.hash || ""}`);
  };

  const toggle = () => setLang(safeLang === "sk" ? "en" : "sk");

  useEffect(() => {
    document.documentElement.lang = safeLang;
  }, [safeLang]);

  const value = useMemo(
    () => ({ lang: safeLang, setLang, toggle, t, homePath, isHome, anchor }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [safeLang, location.pathname, location.hash]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useT = () => useContext(LanguageContext);
